import { Link } from "react-router-dom";
import { Cookie, Shield, BarChart3, Target, Clock, Settings, ChevronRight } from "lucide-react";
import { useCookieConsent } from "../hooks/useCookieConsent";

const COOKIE_TABLE = [
  {
    category: "Necesarias",
    icon: Shield,
    iconColor: "from-emerald-500 to-teal-600",
    duration: "Sesión / 30 días",
    purpose: "Mantener la sesión iniciada, recordar preferencias de idioma y proteger contra fraude.",
    examples: ["plazafy_token", "plazafy_lang", "plazafy_cookie_consent_v1"],
  },
  {
    category: "Analítica",
    icon: BarChart3,
    iconColor: "from-violet-500 to-purple-600",
    duration: "Hasta 24 meses",
    purpose: "Medir el uso de la web de forma agregada y anónima para mejorar la experiencia.",
    examples: ["No usamos Google Analytics ni similares"],
  },
  {
    category: "Marketing",
    icon: Target,
    iconColor: "from-amber-500 to-orange-600",
    duration: "Hasta 90 días",
    purpose: "Permitir a Meta (Facebook/Instagram) mostrarte publicidad relevante de Plazafy.",
    examples: ["_fbp (Meta Pixel)", "fr (Meta)"],
  },
];

export default function CookiesPolicy() {
  const { openSettings } = useCookieConsent();
  return (
    <div className="min-h-screen bg-[#0f0c17] pt-24 pb-16">
      {/* Hero header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 to-transparent pointer-events-none" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-amber-500/5 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-12">
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/30 ring-1 ring-white/10">
              <Cookie className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-tight">
                Política de Cookies
              </h1>
              <p className="text-gray-500 text-sm mt-1">Última actualización: 1 de junio de 2026</p>
            </div>
          </div>
          <p className="text-gray-400 max-w-2xl text-sm sm:text-base">
            Te explicamos qué cookies usamos en Plazafy, para qué sirven y cómo controlarlas
            en cualquier momento. Cumplimos con el RGPD y la LSSI.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* CTA configurar */}
        <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/5 border border-amber-500/20 rounded-2xl p-5 sm:p-6 mb-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
            <Settings className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-white font-medium text-sm sm:text-base">Tus preferencias</h3>
            <p className="text-gray-400 text-xs sm:text-sm mt-0.5">
              Puedes cambiar qué cookies aceptas en cualquier momento.
            </p>
          </div>
          <button
            onClick={openSettings}
            className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 shadow-lg shadow-amber-500/30 hover:shadow-amber-500/50 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200"
            data-testid="cookies-policy-open-settings"
          >
            <Settings className="w-4 h-4 group-hover:rotate-90 transition-transform duration-300" />
            Cambiar preferencias
          </button>
        </div>

        {/* Section 1 */}
        <Section title="¿Qué son las cookies?">
          Las cookies son pequeños archivos de texto que las webs almacenan en tu dispositivo
          cuando las visitás. Sirven para recordar información sobre tu visita, como el idioma
          preferido o si estás logueado. Pueden ser de la propia web (cookies propias) o de
          terceros como Meta, Google o Stripe (cookies de terceros).
        </Section>

        {/* Section 2 */}
        <Section title="Tipos de cookies que usamos en Plazafy">
          <div className="space-y-3 mt-4">
            {COOKIE_TABLE.map((c, i) => (
              <div key={i} className="bg-[#15121e] rounded-2xl border border-white/5 p-5">
                <div className="flex items-start gap-4">
                  <div className={`flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br ${c.iconColor} flex items-center justify-center`}>
                    <c.icon className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <h3 className="text-white font-medium text-base">{c.category}</h3>
                      <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                        <Clock className="w-3 h-3" />
                        {c.duration}
                      </span>
                    </div>
                    <p className="text-gray-400 text-sm mt-2 leading-relaxed">{c.purpose}</p>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {c.examples.map((ex, j) => (
                        <span key={j} className="text-xs px-2.5 py-1 rounded-md bg-white/5 text-gray-400 font-mono">
                          {ex}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* Section 3 */}
        <Section title="Base legal">
          Las cookies <strong className="text-white">necesarias</strong> se cargan en base al
          interés legítimo de prestar el servicio que solicitás (Art. 6.1.f RGPD y Art. 22.2
          LSSI). Las cookies de <strong className="text-white">analítica y marketing</strong> se
          cargan únicamente con tu consentimiento explícito (Art. 6.1.a RGPD y Art. 22.2 LSSI),
          que podés otorgar o revocar en cualquier momento desde el banner o esta página.
        </Section>

        {/* Section 4 */}
        <Section title="Cómo gestionar las cookies en tu navegador">
          Además del panel de Plazafy, podés bloquear o eliminar cookies directamente desde tu
          navegador:
          <ul className="mt-3 space-y-1.5 text-sm">
            <li className="flex items-center gap-2">
              <ChevronRight className="w-3 h-3 text-amber-400" />
              <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-violet-300 hover:text-violet-200 underline">Chrome</a>
            </li>
            <li className="flex items-center gap-2">
              <ChevronRight className="w-3 h-3 text-amber-400" />
              <a href="https://support.mozilla.org/es/kb/habilitar-y-deshabilitar-cookies-sitios-web-rastrear-preferencias" target="_blank" rel="noopener noreferrer" className="text-violet-300 hover:text-violet-200 underline">Firefox</a>
            </li>
            <li className="flex items-center gap-2">
              <ChevronRight className="w-3 h-3 text-amber-400" />
              <a href="https://support.apple.com/es-es/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-violet-300 hover:text-violet-200 underline">Safari</a>
            </li>
            <li className="flex items-center gap-2">
              <ChevronRight className="w-3 h-3 text-amber-400" />
              <a href="https://support.microsoft.com/es-es/microsoft-edge/eliminar-las-cookies-en-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-violet-300 hover:text-violet-200 underline">Edge</a>
            </li>
          </ul>
        </Section>

        {/* Section 5 */}
        <Section title="Cambios en esta política">
          Podemos actualizar esta política para reflejar cambios legales o técnicos. Te avisaremos
          mediante el banner si los cambios afectan a tus preferencias. La fecha de la última
          actualización aparece al inicio del documento.
        </Section>

        {/* Section 6 */}
        <Section title="Contacto">
          Para cualquier consulta sobre el uso de cookies podés escribirnos a{" "}
          <a href="mailto:hola@plazafy.es" className="text-amber-400 hover:text-amber-300 underline">
            hola@plazafy.es
          </a>
          . Más detalles sobre el tratamiento de datos en nuestra{" "}
          <Link to="/privacy" className="text-amber-400 hover:text-amber-300 underline">
            Política de Privacidad
          </Link>
          .
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="text-white text-xl font-semibold mb-3">{title}</h2>
      <div className="text-gray-400 text-sm sm:text-[15px] leading-relaxed">{children}</div>
    </section>
  );
}
